// Expedice na kartě výpisu („Expedujeme dnes / zítra / v pondělí").
//
// Zdrojem je HODINY, ne nativní DOM: `div.delivery-time` Shoptet renderuje jen
// v košíku a na detailu (viz CLAUDE.md), výpis ho nemá. Návrh
// (`design_handoff_category/README.md` §Interakce) proto předepisuje vlastní
// výpočet s cut-off **15:00**: pracovní den před 15:00 → dnes, po 15:00 → nejbližší
// další pracovní den. Uzávěrka je od 2026-10-13 po dnech v týdnu z manifestu
// (`shipping.dispatchCutoff`, pátek 14:30 na zadání klienta) a tenhle modul je
// jediné místo, které ji čte: `beforeCutoff()` rozhoduje „odchází ještě dnes"
// pro celý web, `cutoffLabel()` dává čas do vět („dnes do 14:30").
//
// Svátky jsou součástí pravidla schválně — bez nich by karta 28. 9. slibovala
// „Expedujeme dnes“, i když se ten den neexpeduje. Pohyblivé svátky pokrývá
// Velký pátek a Velikonoční pondělí (Gaussova computus pro gregoriánský kalendář).
//
// POZOR: je to obecné pravidlo skladu, ne termín konkrétního dopravce. Termín
// doručení per dopravce řeší pokladna (checkoutPage.js) z nativního data.
// Karta výpisu a modal košíku ho nečtou napřímo, ale přes `listingDispatch()`
// v deliveryTimeline.js — ta v okně nedělního svozu PPL posune den expedice
// na neděli (sklad sám o sobě v neděli neexpeduje, proto to tady není).

import SHOP from '../../config/shop.js';
import L from '../locale/cs/dispatchEta.js';

// Uzávěrka po dnech v týdnu (0 = neděle), hodiny desetinně: { 1: 15, …, 5: 14.5 }
export const DISPATCH_CUTOFF = SHOP.shipping.dispatchCutoff;

// Popisek se skládá z prefixu a HOLÉ fráze dne (`when`) — tutéž frázi bez
// „Expedujeme" spotřebovává řádek osobního odběru na detailu („odběr v Bílině
// již zítra zdarma"), proto je vystavená zvlášť a ne jen zapečená v labelu.
const LABEL_PREFIX = L.prefix;
const WHEN_TODAY = L.today;
const WHEN_TOMORROW = L.tomorrow;

// 0 = neděle
const DAYS = L.days;

// Pevné státní svátky ČR (měsíc-den)
const FIXED = ['1-1', '5-1', '5-8', '7-5', '7-6', '9-28', '10-28', '11-17', '12-24', '12-25', '12-26'];

// Velikonoční neděle podle anonymního gregoriánského algoritmu
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function sameDay(a, b) {
  return a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function isHoliday(date) {
  return isHolidayIn(date, FIXED);
}

/**
 * Svátek podle seznamu pevných svátků země (`měsíc-den`) + Velký pátek a Velikonoční
 * pondělí (ČR i SR). Expedici řídí svátky ČR (sklad), lhůty pro zákazníka svátky země
 * obchodu (manifest `shop.holidays`, orderStatusModel.returnUntil).
 */
export function isHolidayIn(date, fixed) {
  if (fixed.indexOf(date.getMonth() + 1 + '-' + date.getDate()) >= 0) return true;
  const easter = easterSunday(date.getFullYear());
  const friday = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() - 2);
  const monday = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 1);
  return sameDay(date, friday) || sameDay(date, monday);
}

export function isWorkday(date) {
  const d = date.getDay();
  return d !== 0 && d !== 6 && !isHoliday(date);
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Uzávěrka expedice dne `date` v hodinách desetinně (pátek 14.5); mimo pracovní den null. */
export function cutoffHour(date) {
  if (!isWorkday(date)) return null;
  const h = DISPATCH_CUTOFF[date.getDay()];
  return h == null ? null : h;
}

/** Odchází objednávka z okamžiku `ref` ještě týž den? (pracovní den před jeho uzávěrkou) */
export function beforeCutoff(ref) {
  const h = cutoffHour(ref);
  return h != null && ref.getHours() * 60 + ref.getMinutes() < Math.round(h * 60);
}

/** Hodiny desetinně jako čas do věty: 14.5 → „14:30", 15 → „15:00". */
export function hourLabel(h) {
  const min = Math.round(h * 60);
  return Math.floor(min / 60) + ':' + String(min % 60).padStart(2, '0');
}

/** Uzávěrka dne `date` jako čas („14:30"); mimo pracovní den null. */
export function cutoffLabel(date) {
  const h = cutoffHour(date);
  return h == null ? null : hourLabel(h);
}

/**
 * Popisek expedice pro zadaný DEN expedice `date`, viděný z okamžiku `now`
 * (volitelný, kvůli testům). Vrací `{ label, when, days }` — `when` je holá
 * fráze dne („dnes" / „zítra" / „v pondělí"), `days` posun ve dnech od dneška
 * (0 = dnes).
 */
export function dispatchOn(date, now) {
  const ref = now ? new Date(now.getTime()) : new Date();
  const today = startOfDay(ref);
  const day = startOfDay(date);
  const days = Math.round((day - today) / 86400000);
  if (days <= 0) return { label: LABEL_PREFIX + WHEN_TODAY, when: WHEN_TODAY, days: 0 };
  // „zítra" jen když se dnes normálně expeduje a jen se minul cut-off; v neděli
  // je zítřek sice pondělí, ale návrh chce pojmenovaný den („Expedujeme
  // v pondělí") — přes víkend a svátek je konkrétní den srozumitelnější
  const soon = isWorkday(today) && days === 1;
  const when = soon ? WHEN_TOMORROW : DAYS[day.getDay()];
  return { label: LABEL_PREFIX + when, when: when, days: days };
}

/**
 * Popisek expedice podle pravidla skladu pro daný okamžik. `now` je volitelný
 * (kvůli testům). Tvar jako `dispatchOn`.
 */
export function dispatchEta(now) {
  const ref = now ? new Date(now.getTime()) : new Date();
  const today = startOfDay(ref);

  if (beforeCutoff(ref)) return dispatchOn(today, ref);

  // nejbližší další pracovní den (strop je pojistka proti nekonečné smyčce)
  const next = new Date(today.getTime());
  for (let i = 0; i < 14; i += 1) {
    next.setDate(next.getDate() + 1);
    if (isWorkday(next)) return dispatchOn(next, ref);
  }
  return { label: LABEL_PREFIX + WHEN_TOMORROW, when: WHEN_TOMORROW, days: 1 };
}
